import { useState } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useQueryClient } from "@tanstack/react-query"
import { Input } from "@base-ui/react/input"
import { toast } from "sonner"
import {
  AuthShell,
  OtpInput,
  PrimaryButton,
  TextLink,
  fieldCls,
  labelCls,
} from "@/components/auth/AuthShell"
import {
  sendEmailOtp,
  signInWithOtp,
  signInWithPassword,
  takeOAuthRedirectUrl,
} from "@/core/auth"
import { resetEventsStore, useEvents } from "@/core/active-event"
import { resetUserOrgsStore, useUserOrgs } from "@/core/orgs"

export const Route = createFileRoute("/sign-in")({
  validateSearch: (search: Record<string, unknown>): { next?: string } => ({
    next: typeof search.next === "string" ? search.next : undefined,
  }),
  component: SignIn,
})

function SignIn() {
  const navigate = useNavigate()
  const { next } = Route.useSearch()
  const queryClient = useQueryClient()
  const [stage, setStage] = useState<"email" | "otp">("email")
  const [mode, setMode] = useState<"otp" | "password">("otp")
  const [email, setEmail] = useState("")
  const [otp, setOtp] = useState("")
  const [password, setPassword] = useState("")
  const [busy, setBusy] = useState(false)
  const [resending, setResending] = useState(false)

  async function completeSignIn() {
    const oauthRedirect = takeOAuthRedirectUrl()
    if (oauthRedirect) {
      window.location.assign(oauthRedirect)
      return
    }
    queryClient.clear()
    resetEventsStore()
    resetUserOrgsStore()
    await Promise.allSettled([
      useEvents.getState().load(),
      useUserOrgs.getState().reload(),
    ])
    await navigate({ to: resolveNext(next), replace: true })
  }

  async function onPasswordSignIn(e: React.FormEvent) {
    e.preventDefault()
    if (!email || !password) return
    setBusy(true)
    try {
      await signInWithPassword(email, password)
      await completeSignIn()
    } catch {
      toast.error("Wrong email or password — or this account uses sign-in codes.")
    } finally {
      setBusy(false)
    }
  }

  async function onSendOtp(e: React.FormEvent) {
    e.preventDefault()
    if (!email) return
    setBusy(true)
    try {
      await sendEmailOtp(email)
      toast.success(`Code sent to ${email}`)
      setStage("otp")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't send code")
    } finally {
      setBusy(false)
    }
  }

  async function onVerifyOtp(e?: React.FormEvent, code?: string) {
    e?.preventDefault()
    const value = code ?? otp
    if (!email || value.length !== 6 || busy) return
    setBusy(true)
    try {
      await signInWithOtp(email, value)
      await completeSignIn()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "That code didn't work")
    } finally {
      setBusy(false)
    }
  }

  async function onResend() {
    if (!email) return
    setResending(true)
    try {
      await sendEmailOtp(email)
      toast.success(`New code sent to ${email}`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't resend")
    } finally {
      setResending(false)
    }
  }

  return (
    <AuthShell title="Organizer Sign in" wave>
      {mode === "password" ? (
        <form onSubmit={onPasswordSignIn} className="space-y-4">
          <div>
            <label htmlFor="pw-email" className={`${labelCls} text-center`}>Email</label>
            <Input
              id="pw-email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={fieldCls}
            />
          </div>
          <div>
            <label htmlFor="pw-password" className={`${labelCls} text-center`}>Password</label>
            <Input
              id="pw-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={fieldCls}
            />
          </div>
          <PrimaryButton disabled={busy || !email || !password}>
            {busy ? "Signing in…" : "Sign in"}
          </PrimaryButton>
          <div className="text-center">
            <TextLink onClick={() => setMode("otp")}>Use a sign-in code instead</TextLink>
          </div>
        </form>
      ) : stage === "email" ? (
        <form onSubmit={onSendOtp} className="space-y-4">
          <div>
            <label htmlFor="email" className={`${labelCls} text-center`}>Email</label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={fieldCls}
            />
          </div>
          <PrimaryButton disabled={busy || !email}>
            {busy ? "Sending…" : "Send sign-in code"}
          </PrimaryButton>
          <div className="text-center">
            <TextLink onClick={() => setMode("password")}>Sign in with password instead</TextLink>
          </div>
        </form>
      ) : (
        <form onSubmit={onVerifyOtp} className="space-y-4">
          <div>
            <label className={`${labelCls} text-center`}>Enter your sign-in code</label>
            <OtpInput
              value={otp}
              onChange={setOtp}
              autoFocus
              onComplete={(v) => void onVerifyOtp(undefined, v)}
            />
            <p className="mt-2.5 text-center text-[12px] text-white/55">
              Sent to <span className="font-medium text-white/80">{email}</span>
            </p>
          </div>
          <PrimaryButton disabled={busy || otp.length !== 6}>
            {busy ? "Verifying…" : "Sign in"}
          </PrimaryButton>
          <div className="flex items-center justify-between">
            <TextLink onClick={() => setStage("email")}>← Different email</TextLink>
            <TextLink disabled={resending} onClick={onResend}>
              {resending ? "Resending…" : "Resend code"}
            </TextLink>
          </div>
        </form>
      )}
    </AuthShell>
  )
}

const RESERVED = new Set([
  "accept-invitation", "account", "api", "auth", "broadcast", "health", "invite",
  "openapi.json", "realtime", "set-password", "sign-in",
])

// Only allow same-origin app paths as the post-sign-in destination.
function resolveNext(next: string | undefined): string {
  if (!next || !next.startsWith("/")) return "/"
  const first = next.split(/[?#]/, 1)[0]?.split("/").filter(Boolean)[0]
  return !first || !RESERVED.has(first) ? next : "/"
}
